import { useEffect, useEffectEvent, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { EMAIL_INDEX_KEY } from '@/hooks/use-email-index/email-index-key';
import { knowledgeKeys } from '@/hooks/use-knowledge-keys';
import { TASK_QUERY_KEYS } from '@/hooks/task-query-keys';
import { officeAgentKeys } from '@/hooks/use-office-agents';
import { OFFICE_CHANGED_EVENTS } from '@/hooks/tasks-sync-channel';
import { refreshQueries } from '@/lib/refresh-queries';

import type { OfficeChangedEvent } from '@/hooks/tasks-sync-channel';
import type { RealtimeBroadcastSubscription } from '@/hooks/use-realtime-channel';

/** A burst of pokes about one read is read once. */
export const OFFICE_SYNC_DEBOUNCE_MS = 500;

const EVENTS = Object.values(OFFICE_CHANGED_EVENTS);

export interface OfficeSyncRefresh {
  /**
   * The office pokes to listen for on the office's tasks-sync room. Always the same four, so the
   * room's channel is never rebuilt when the office's gates are read; they read nothing while off.
   */
  readonly subscriptions: RealtimeBroadcastSubscription[];
  /** Reads every one of them again, for a rejoin after which pokes may have been missed. */
  readonly reconcile: () => void;
}

/**
 * What the agentic home reads again when the office pokes it (hooks/tasks-sync-channel.ts,
 * `OFFICE_CHANGED_EVENTS`): approvals, the office's agents, the size of the library, the tasks due
 * to the person. Each read is refreshed only where it is shown; a hidden home is caught up when
 * shown again. While a save or archive of the person's own agents is out, a poke about agents waits
 * for it, since that write's own read of the list may have been made before the colleague's change.
 * Where the agentic home is off (`isOn` false, as it is while the gates load), nothing is read.
 */
export function useOfficeSyncRefresh(
  organizationId: string | undefined,
  isOn: boolean,
): OfficeSyncRefresh {
  const queryClient = useQueryClient();
  const timersRef = useRef(new Map<OfficeChangedEvent, ReturnType<typeof setTimeout>>());

  const read = (orgId: string, event: OfficeChangedEvent): void => {
    if (event === OFFICE_CHANGED_EVENTS.approvals) {
      refreshQueries(queryClient, { queryKey: [EMAIL_INDEX_KEY, orgId] });
      return;
    }
    if (event === OFFICE_CHANGED_EVENTS.tasksDue) {
      refreshQueries(queryClient, { queryKey: [TASK_QUERY_KEYS.myTasksByOrg, 'due'] });
      return;
    }
    if (event === OFFICE_CHANGED_EVENTS.knowledge) {
      refreshQueries(queryClient, { queryKey: knowledgeKeys.aggregateAll(orgId) });
      return;
    }
    if (queryClient.isMutating({ mutationKey: officeAgentKeys.writes(orgId) }) > 0) {
      schedule(orgId, event);
      return;
    }
    refreshQueries(queryClient, { queryKey: officeAgentKeys.list(orgId), exact: true });
  };

  const schedule = (orgId: string, event: OfficeChangedEvent): void => {
    const timers = timersRef.current;
    clearTimeout(timers.get(event));
    timers.set(
      event,
      setTimeout(() => {
        timers.delete(event);
        read(orgId, event);
      }, OFFICE_SYNC_DEBOUNCE_MS),
    );
  };

  // Pending reads belong to the office they were scheduled for. Switching on (the gates read, or
  // another office) reads everything once: a poke sent before then was not acted on.
  const readAll = useEffectEvent((orgId: string) => {
    for (const event of EVENTS) {
      schedule(orgId, event);
    }
  });
  useEffect(() => {
    const timers = timersRef.current;
    if (isOn && organizationId) {
      readAll(organizationId);
    }
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, [organizationId, isOn]);

  const scheduleIfOn = (event: OfficeChangedEvent): void => {
    if (isOn && organizationId) {
      schedule(organizationId, event);
    }
  };
  return {
    // Content-free: the payload is never read.
    subscriptions: EVENTS.map((event) => ({ event, onPayload: () => scheduleIfOn(event) })),
    reconcile: () => {
      for (const event of EVENTS) {
        scheduleIfOn(event);
      }
    },
  };
}
